import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck, MapPin } from "lucide-react";
import { ASSETS } from "@/constants/assets";

const headline = ["Professional", "Carpet & Upholstery", "Cleaning in Lanarkshire"];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 pb-16 sm:pt-36 lg:pt-40 lg:pb-24"
    >
      {/* soft background accents */}
      <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-turquoise-soft/50 blur-3xl" />
      <div className="pointer-events-none absolute top-40 right-0 h-80 w-80 rounded-full bg-turquoise/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-10 items-center">
        {/* Left: copy */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-navy ring-1 ring-navy/10 shadow-sm"
          >
            <Sparkles className="h-3.5 w-3.5 text-turquoise" />
            Carpet · Rug · Upholstery
          </motion.div>

          <h1 className="mt-6 font-display fluid-h1 font-semibold text-navy">
            {headline.map((line, i) => (
              <span key={line} className="line-mask pb-[0.06em]">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                  className="block"
                >
                  {i === 1 ? (
                    <span className="italic text-turquoise">{line}</span>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-6 max-w-xl text-lg text-navy/70 leading-relaxed"
          >
            Reliable, affordable carpet, rug and upholstery cleaning with fresh
            results you can see.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-quote-btn"
              className="group inline-flex items-center gap-2.5 rounded-full bg-navy px-7 py-4 text-base font-semibold text-white shadow-lg shadow-navy/20 transition-all duration-300 hover:bg-turquoise hover:shadow-turquoise/40 hover:-translate-y-0.5"
            >
              Get a Free Quote
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#results"
              data-testid="hero-results-btn"
              className="group inline-flex items-center gap-2.5 rounded-full bg-white px-7 py-4 text-base font-semibold text-navy ring-1 ring-navy/15 transition-all duration-300 hover:ring-turquoise hover:-translate-y-0.5"
            >
              View Results
              <span className="h-2 w-2 rounded-full bg-turquoise transition-transform duration-300 group-hover:scale-150" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-10 flex flex-wrap gap-x-7 gap-y-3 text-sm font-medium text-navy/70"
          >
            <span className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-turquoise" /> Fully insured & reliable
            </span>
            <span className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-turquoise" /> Based in Airdrie
            </span>
          </motion.div>
        </div>

        {/* Right: framed van photo with parallax */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="absolute -top-4 -right-4 h-28 w-28 rounded-2xl border-2 border-turquoise/40 hidden sm:block" />
          <div className="relative overflow-hidden rounded-[1.6rem] shadow-2xl shadow-navy/25 ring-1 ring-navy/10">
            <motion.img
              src={ASSETS.van}
              alt="Lanarkshire Cleaning Company carpet cleaning van"
              style={{ y: imgY, scale: imgScale }}
              className="w-full h-[400px] sm:h-[520px] lg:h-[600px] object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/45 via-transparent to-transparent" />
            {/* floating badge */}
            <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between rounded-2xl bg-white/90 backdrop-blur-md px-5 py-3.5 shadow-lg">
              <div>
                <p className="text-xs uppercase tracking-widest text-navy/50 font-semibold">
                  Fresh results
                </p>
                <p className="font-display text-lg text-navy leading-tight">
                  Across Lanarkshire
                </p>
              </div>
              <div className="flex -space-x-1 text-turquoise">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="h-4 w-4 fill-current" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                  </svg>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
